import {ChangeDetectionStrategy, Component, Input, OnInit, SimpleChanges, OnChanges} from '@angular/core';
import {ApexAxisChartSeries, ApexOptions, ApexResponsive} from 'ng-apexcharts';
import {DatePipe} from '@angular/common';

import {TerraNumberPipe} from '../../../../shared/pipes/TerraNumber.pipe';

export interface LeverageGraphInput {
  date: Date;
  withoutLeverage: number;
  withLeverage: number;
}

export enum GraphPointOption {
  withoutLeverage = 0,
  withLeverage = 1,
  total = 2
}

@Component({
  selector: 'terra-lp-leverage-graph',
  templateUrl: './lp-leverage-graph.component.html',
  styleUrls: ['./lp-leverage-graph.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class LpLeverageGraphComponent implements OnInit, OnChanges {
  @Input() data: LeverageGraphInput[];
  @Input() dist: number;
  @Input() app: number;

  public chartOptions: Partial<ApexOptions>;

  constructor(private datePipe: DatePipe,
              private numberPipe: TerraNumberPipe) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasCurrentDist = changes.dist?.currentValue !== undefined && changes.dist?.currentValue !== null;
    const hasCurrentApp = changes.app?.currentValue !== undefined && changes.app?.currentValue !== null;
    if (hasCurrentDist || hasCurrentApp) {
      this.initChart();
    } else if (changes.data?.currentValue) {
      let anyChange = false;
      const previousValue = changes.data.previousValue as LeverageGraphInput[];
      const currentValue = changes.data.currentValue as LeverageGraphInput[];
      currentValue.forEach((point, i) => {
        const prevDate = previousValue[i].date;
        const prevWithoutLeverage = previousValue[i].withoutLeverage;
        const prevWithLeverage = previousValue[i].withLeverage;
        if (point.date.toDateString() !== prevDate.toDateString() || point.withoutLeverage !== prevWithoutLeverage || point.withLeverage !== prevWithLeverage) {
          anyChange = true;
        }
      });
      if (anyChange) {
        this.initChart();
      }
    }
  }

  private initChart() {
    this.chartOptions = {
      series: this.getDataSeries(),
      chart: {
        type: 'line',
        height: '450',
        stacked: false,
        toolbar: {
          show: true,
        },
        events: {
          selection: (chart, e) => {
            console.log(new Date(e.xaxis.min));
          },
        },
      },
      stroke: {
        curve: 'straight',
        width: 3,
      },
      markers: {
        size: 5,
        hover: {
          size: 6
        }
      },
      fill: {
        type: 'solid',
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
      grid: {
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 10,
        },
      },
      xaxis: {
        type: 'category',

        labels: {
          formatter: (dateString: string, timestamp?: number, opts?: any) =>
            this.datePipe.transform(dateString, `MMM yy`),
          hideOverlappingLabels: true,
        },
        tickAmount: this.data.length <= 20 ? 'dataPoints' : 20,
      },
      yaxis: {
        max: this.getMaxYValue(),
        decimalsInFloat: 1,
        labels: {
          formatter: (val: number, opts?: any) => {
            return this.numberPipe.transform(Math.round(val / 1000), 1) + 'K';
          },
        },
      },
      tooltip: {
        y: {
          formatter: (y: number) => {
            if (typeof y !== 'undefined') {
              return `$${this.numberPipe.transform(y)}`;
            }
            return y;
          },
        },
      },
      responsive: [
        {
          breakpoint: 650,
          options: {
            chart: {
              toolbar: {
                show: false,
              },
            },
            xaxis: {
              tickAmount: this.data.length <= 8 ? 'dataPoints' : 8,
            }
          },
        } as ApexResponsive,
      ],
    };
  }

  private getDataSeries(): ApexAxisChartSeries {
    const dataSeries: ApexAxisChartSeries = [];
    dataSeries.push({
      name: 'With Leverage',
      type: 'line',
      color: '#002563', // '#3D41EB',
      data: this.convertToSeries(GraphPointOption.withLeverage),
    });
    dataSeries.push({
      name: 'Without Leverage',
      type: 'line',
      color: '#FF0000',
      data: this.convertToSeries(GraphPointOption.withoutLeverage),
    });

    return dataSeries;
  }

  private convertToSeries(option: GraphPointOption): Array<{ x: number; y: number }> {
    const series = [];
    switch (option) {
      case GraphPointOption.withoutLeverage:
        this.data.forEach(point => {
          series.push({x: point.date, y: point.withoutLeverage});
        });
        break;
      case GraphPointOption.withLeverage:
        this.data.forEach(point => {
          series.push({x: point.date, y: point.withLeverage});
        });
        break;
    }

    return series;
  }

  private getMaxYValue(): number {
    const lastIndex = this.data.length - 1;
    return Math.max(this.data[lastIndex].withoutLeverage, this.data[lastIndex].withLeverage);
  }
}
